import React, { ReactElement, SyntheticEvent, useState } from 'react';
import styles from './forgotPasswordForm.module.scss';
import { PageTitleWithTracking as Title } from '@mo/page-title';
import { SimpleInput } from '@mo/simple-form';
import { LoadableButton } from '@mo/loadable-button';
import { BackButton } from '@mo/back-button';
import { PageErrorBox } from '@mo/page-error-box';
import { isEmpty } from 'lodash';
import { mapPageValidationErrors, validate } from '../validation/forgotPasswordValidation';
import { resetPassword } from '../forgotPasswordGateway';
import { TechnicalError } from '../../../components/technicalError/TechnicalError';
import { useHistory } from 'react-router-dom';
import { useQueryParam } from '@mo/hooks';
import { BASE_PATH } from '../../../common/routes';

export interface ForgotPasswordFormProps {
    setSubmittedUsername: (username: string) => void;
}

function ForgotPasswordForm({ setSubmittedUsername }: ForgotPasswordFormProps): ReactElement {
    const { utm_source } = useQueryParam(window.location.href);
    const [username, setUsername] = useState('');
    const [loading, setLoading] = useState(false);
    const [technicalError, setTechnicalError] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string[] }>({});

    const history = useHistory();
    const displayBackButton = utm_source !== 'email';

    const validateAndSubmit = async (event: SyntheticEvent): Promise<void> => {
        event.preventDefault();
        try {
            const trimmedUsername = username?.trim();
            setValidationErrors({});
            await validate({ username: trimmedUsername });
            await submitResetPasswordRequest(trimmedUsername);
        } catch (errors: any) {
            setValidationErrors(errors);
        }
    };

    const submitResetPasswordRequest = async (username: string): Promise<void> => {
        try {
            setLoading(true);
            await resetPassword(username);
            setSubmittedUsername(username);
        } catch (_error) {
            setLoading(false);
            setTechnicalError(true);
        }
    };

    const goBack = (): void => {
        if (window.history.length <= 1) {
            history.replace(BASE_PATH);
        } else {
            history.go(-1);
        }
    };

    return (
        <>
            <div className={styles.formHeader}>
                <div className={styles.formHeaderContent}>
                    {displayBackButton && <BackButton onClick={goBack} text='Back' />}
                    <PageErrorBox
                        hasErrors={!isEmpty(validationErrors)}
                        errors={mapPageValidationErrors(validationErrors)}
                        className={styles.pageErrorBox}
                    />
                    <Title gaTitle={'Forgot password - Please enter your email address'} text='Please enter your email address' />
                    <p className={styles.helpText}>We’ll send you an email to reset your password.</p>
                </div>
            </div>

            <form>
                <SimpleInput
                    inputFieldClassName={styles.inputField}
                    groupClassName={styles.inputGroup}
                    labelText='Email Address'
                    inputName='username'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setValidationErrors({});
                        setUsername(e.target.value);
                    }}
                    value={username}
                    autoCapitalize={'none'}
                    autoComplete={'username'}
                    type={'text'}
                    errors={validationErrors && validationErrors.username}
                />

                {technicalError && (
                    <TechnicalError>
                        Sorry, we're having some technical difficulties. Please try again 
                        in a few moments. If the problem persist please contact your team 
                        at Manufacturer Relations <a href='mailto:manufacturerrelations@motabilityoperations.co.uk'>manufacturerrelations@motabilityoperations.co.uk </a> 
                        or DFW and adaptations <a href='mailto:dfwandadaptation@motabilityoperations.co.uk'>dfwandadaptation@motabilityoperations.co.uk</a>.
                    </TechnicalError>
                )}

                <LoadableButton
                    loading={loading}
                    loadingText='Sending'
                    notLoadingText='Send email'
                    className={styles.sendEmailButton}
                    classNameLoading={styles.sendEmailButtonLoading}
                    data-ga='send-email-button'
                    onClick={validateAndSubmit}
                />
            </form>
        </>
    );
}

export default ForgotPasswordForm;
